import { supabase } from "@/auth/supabase-client";
import { ensureFreshSession, withSupabaseTimeout } from "@/auth/session-guard";
import type {
  ConsultantAccessLinkResult,
  ConsultantAccessRow,
  ConsultantRoleResult,
} from "./types";

const FUNCTION_NAME = "manage_consultant_access";
const TIMEOUT_MS = 60_000;

async function messageFor(error: unknown): Promise<string> {
  const context = (error as { context?: Response }).context;
  if (context && typeof context.json === "function") {
    try {
      const parsed = (await context.json()) as { error?: { message?: string }; message?: string };
      if (typeof parsed.error?.message === "string") return parsed.error.message;
      if (typeof parsed.message === "string") return parsed.message;
    } catch {
      // Provider message below is the fallback for non-JSON responses.
    }
  }
  return error instanceof Error ? error.message : String(error);
}

async function invoke<T>(body: Record<string, unknown>): Promise<T> {
  await ensureFreshSession();
  const { data, error } = await withSupabaseTimeout(
    supabase.functions.invoke(FUNCTION_NAME, { body }),
    "Consultant access request",
    TIMEOUT_MS
  );
  if (error) throw new Error(await messageFor(error));
  const envelope = data as { data?: T } | null;
  if (!envelope?.data) throw new Error("Consultant access response was malformed.");
  return envelope.data;
}

export async function listConsultantAccess(): Promise<ConsultantAccessRow[]> {
  const result = await invoke<{ consultants: ConsultantAccessRow[] }>({ mode: "list" });
  return result.consultants;
}

export function activateConsultantAccess(userId: string): Promise<ConsultantAccessLinkResult> {
  return invoke({ mode: "activate", user_id: userId });
}

export function createConsultantRecoveryLink(userId: string): Promise<ConsultantAccessLinkResult> {
  return invoke({ mode: "recovery_link", user_id: userId });
}

export function setConsultantAdminRole(
  userId: string,
  role: "user" | "admin"
): Promise<ConsultantRoleResult> {
  return invoke({ mode: "set_role", user_id: userId, role });
}
